// /pages/DeviceCodeAuth.tsx
import React, { useContext, useMemo, useState } from "react";

// Context / Hooks
import { UserAuthenticationContext } from "../../../Context/UserAuthenticationContext";
import { useConfirmDeviceCode } from "../../../Hooks/Auth/useConfirmDeviceCode";

// Components
import InputField from "../../../Components/Inputs/InputField";
import ButtonPill from "../../../Components/Buttons/ButtonPill";

// Constants
import { RoutePaths } from "../../../Constants/RoutePaths";

// Assets (same as TvAuthenticationScreen layout)
import { ReactComponent as IckonicLogoQuestionEverything } from "../../../Assets/Images/Logos/ickonic-question-everything-white.svg";
import IconCheckMarkHollow from "../../../Assets/SVGs/Icons/YesOrNo/CheckMarkHollow";

const DeviceCodeAuth = () => {
    const { getValidAccessToken, doesAuthTokenExist } = useContext(UserAuthenticationContext);

    const { confirmDeviceCode, isConfirming, lastErrorMessage, clearConfirmError } =
        useConfirmDeviceCode({ getValidAccessToken });

    const [code, setCode] = useState("");
    const [success, setSuccess] = useState(false);

    const authed = doesAuthTokenExist();

    const prettyCode = useMemo(() => {
        const clean = code.replace(/[^a-z0-9]/gi, "").toUpperCase();
        if (clean.length <= 3) return clean;
        return clean.slice(0, 3) + " " + clean.slice(3, 6);
    }, [code]);

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!authed || isConfirming) return;

        clearConfirmError();
        setSuccess(false);

        const res = await confirmDeviceCode(code);
        if (res.ok) setSuccess(true);
    };

    const codeForm = (
        <>
            <p style={{marginTop: 0}}>
                Enter the code shown on your TV to link this device to your account.
            </p>

            <form onSubmit={onSubmit}>
                <InputField
                    type="text"
                    name="Device Code"
                    value={code}
                    disabled={!authed || isConfirming}
                    label="Device Code"
                    onChange={(e) => {
                        setCode(e.target.value);
                        if (lastErrorMessage) clearConfirmError();
                        if (success) setSuccess(false);
                    }}
                />

                <div className="tv-apps-auth__button-wrapper">
                    <ButtonPill
                        type="submit"
                        disabled={!authed || isConfirming || !code.trim()}
                        label={isConfirming ? "Linking…" : "Link device"}
                        className={`button-wrapper__submit ${code.trim() ? "has-value" : "has-no-value"}`}
                    />
                </div>
            </form>

            {lastErrorMessage && (
                <div className="tv-apps-auth__error" style={{marginTop: 12}}>
                    ❌ {lastErrorMessage}
                </div>
            )}
        </>
    );

    const unauthenticatedLayout = (
        <>
            <h3>You need to be logged in to link your TV.</h3>

            <div className="tv-apps-auth__button-wrapper">
                <ButtonPill label="Sign Up" link={RoutePaths.SignUp[0]} />

                <p>Or</p>

                <ButtonPill label="Login" link={RoutePaths.Login + `?redirectTo=activate`} />
            </div>
        </>
    );

    const successLayout = (
        <div className="tv-apps-auth__success">
            {IconCheckMarkHollow()}
            Success! Your TV should log in shortly. You can go back to your TV now.
        </div>
    );

    const DisplayLayout = () => {
        if (success) return successLayout;
        if (authed) return codeForm;
        return unauthenticatedLayout;
    };

    return (
        <div className="page page--tv-app-auth">
            <div className="tv-apps-auth__inner">
                <h2>Link your TV</h2>

                <div className="tv-apps-auth__divider" />

                <div className="tv-apps-auth__content">
                    {DisplayLayout()}
                </div>
            </div>
        </div>
    );
};

export default DeviceCodeAuth;
